import { useMemo } from "react";
import { useOrderById } from "./useOrderById";
import { useMutation } from "@apollo/client";
import {
  AddOrderCommentDocument,
  CreateOrderCommentAttachmentDocument,
  DeleteOrderCommentAttachmentDocument,
  GetOrderByIdDocument,
  UpdateOrderCommentDocument,
} from "../../gql/generated/graphql";
import { usePresignedFileUpload } from "../files/usePresignedFileUpload";

interface Props {
  orderId: string;
}

const sortCommentsByDate = (
  commentA: { createdAt: string },
  commentB: { createdAt: string }
) => {
  return (
    new Date(commentB.createdAt).getTime() -
    new Date(commentA.createdAt).getTime()
  );
};

export const useOrderComment = ({ orderId }: Props) => {
  const {
    order,
    isLoading: isOrderByIdLoading,
    error: orderByIdError,
    isEditable,
    refetch: refetchOrder,
  } = useOrderById({ orderId });

  const [addOrderComment] = useMutation(AddOrderCommentDocument, {
    refetchQueries: [GetOrderByIdDocument],
  });
  const [updateOrderComment] = useMutation(UpdateOrderCommentDocument, {
    refetchQueries: [GetOrderByIdDocument],
  });
  const [createOrderCommentAttachment] = useMutation(
    CreateOrderCommentAttachmentDocument
  );
  const [deleteOrderCommentAttachment] = useMutation(
    DeleteOrderCommentAttachmentDocument,
    {
      refetchQueries: [GetOrderByIdDocument],
    }
  );
  const { uploadFile } = usePresignedFileUpload();

  const { activeOrderComment, previousOrderComments } = useMemo(() => {
    const sortedOrderComments = [...(order?.comments || [])].sort(
      sortCommentsByDate
    );

    return {
      activeOrderComment: sortedOrderComments[0],
      previousOrderComments: sortedOrderComments.slice(1),
    };
  }, [order?.comments]);

  const upsertActiveOrderComment = async (comment: string) => {
    if (!isEditable) {
      throw new Error("Order is not editable");
    }

    if (activeOrderComment) {
      const res = await updateOrderComment({
        variables: {
          orderId,
          orderCommentId: activeOrderComment.id,
          comment,
        },
      });

      const updatedComment = res.data?.updateOrderComment.comments.find(
        (c: { id: string }) => c.id === activeOrderComment.id
      );

      if (!updatedComment) {
        throw new Error("Error updating comment");
      }

      return updatedComment;
    } else {
      const res = await addOrderComment({
        variables: {
          orderId,
          comment,
        },
      });

      const newComment =
        res.data?.addOrderComment.comments.sort(sortCommentsByDate)[0];

      if (!newComment) {
        throw new Error("Error creating new comment");
      }

      return newComment;
    }
  };

  const uploadActiveOrderCommentAttachment = async (file: File) => {
    if (!isEditable) {
      throw new Error("Order is not editable");
    }

    const orderComment = activeOrderComment
      ? activeOrderComment
      : await upsertActiveOrderComment("");

    const res = await createOrderCommentAttachment({
      variables: {
        orderId,
        orderCommentId: orderComment.id,
        filename: file.name,
      },
    });

    if (res.errors || !res.data?.createOrderCommentAttachment) {
      throw new Error("Error creating order comment attachment");
    }

    await uploadFile(res.data.createOrderCommentAttachment, file);

    await refetchOrder();
  };

  const removeActiveOrderCommentAttachment = async (fileId: string) => {
    if (!isEditable) {
      throw new Error("Order is not editable");
    }

    if (!activeOrderComment) {
      throw new Error("No active order comment to remove attachment from");
    }

    await deleteOrderCommentAttachment({
      variables: {
        orderId,
        orderCommentId: activeOrderComment.id,
        fileId,
      },
    });
  };

  return {
    activeOrderComment,
    previousOrderComments,
    isLoading: isOrderByIdLoading,
    error: orderByIdError,
    upsertActiveOrderComment,
    uploadActiveOrderCommentAttachment,
    removeActiveOrderCommentAttachment,
    refetchOrder,
  };
};
